import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { answerBlock } from 'actions';

import { QuestionAddressBlock } from 'components/Questionnaires/blocks';
import { QuestionTextField, QuestionUsTinField } from 'components/Questionnaires/fields';
import { validateQuestion, setValidators, isEmptyObject, requiredValidator, lengthValidator, sameCharacterValidator, sequentialIntegerValidator } from 'components/Questionnaires/validators';
import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import Question from 'components/Questionnaires/Question';

export const QuestionSubstantialOwnerBlock = ({ block, onAnswer, preQualAnswers }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block.answers ?? {});

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const [name, setName] = useState({
    id: 'name',
    component: QuestionTextField,
    label: 'questionnaire.substantialOwner.name.label',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.substantialOwner.name.errorMessage' }
      ]
    },
    answer: answer?.name
  });

  const [tin, setTin] = useState({
    id: 'tin',
    component: QuestionUsTinField,
    label: 'questionnaire.usTin',
    info: {
      instructions: 'questionnaire.usTin.tin.info',
    },
    componentProps: {
      tinFormat: 'SSN',
      length: 9
    },
    visible: true,
    validation: setValidators(
      true,
      [
        { condition: lengthValidator, message: 'questionnaire.usTin.tin.lengthErrorMessage' },
        { condition: sameCharacterValidator, message: 'questionnaire.usTin.tin.sameCharErrorMessage' },
        { condition: sequentialIntegerValidator, message: 'questionnaire.usTin.tin.sequentialErrorMessage' },
      ],
      'questionnaire.usTin.tin.errorMessage'
    ),
    answer: answer?.tin
  });

  const [address, setAddress] = useState({
    id: 'address',
    component: QuestionAddressBlock,
    info: {
      instructions: 'questionnaire.address.1.info'
    },
    componentProps: {
      line1Footnote: 'questionnaire.address.1.footnote.v2'
    },
    className: 'p-0 mt-0 no-border nested',
    visible: true,
    hideHeader: true
  });

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'name': {
        const newName = { ...q, answer: value };
        validateQuestion(newName);
        setName(newName);
        setAnswer({ ...answer, name: value });
        break;
      }
      case 'tin': {
        const newTin = { ...q, answer: value };
        validateQuestion(newTin);
        setTin(newTin);
        setAnswer({ ...answer, tin: value });
        break;
      }
      default:
        break;
    }
  };

  const onAddressAnswer = (b, a, v) => {
    const newBlock = { ...b, ...{ answer: a, isValid: v } };
    setAddress(newBlock);
    setAnswer({ ...answer, address: a });
  };

  const isValid = () => {
    if (name.answer === undefined
      && tin.answer === undefined) {
      return undefined;
    }

    if (isEmptyObject(address.answer)) {
      return name.isValid && tin.isValid;
    }

    return name.isValid && tin.isValid && address.isValid;
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (answer?.name === undefined || answer.name === '') {
        onQuestionChange({ ...name, isForceEdited: true }, '');
      }
      if (answer?.tin === undefined || answer.tin === '') {
        onQuestionChange({ ...tin, isForceEdited: true }, '');
      }
      if (!answer.address || isEmptyObject(answer.address)) {
        const newBlock = { ...address, ...{ isForceEdited: true } };
        setAddress(newBlock);
      }
    }
  }, [block.isForceEdited]);

  return (
    <div className="questions container">
      <div className="row">
        <Question
          key={ name.id }
          blockId={ blockIdentifier }
          question={ name }
          { ...name.componentProps }
          onChange={ (q, a) => onQuestionChange(q, a) }
        />
      </div>
      <div className="row">
        <Question
          key={ tin.id }
          blockId={ blockIdentifier }
          question={ tin }
          { ...tin.componentProps }
          onChange={ (q, a) => onQuestionChange(q, a) }
        />
      </div>
      <div>
        <QuestionBlock
          questionnaireType={ QuestionnaireType.Form }
          key={ `${ blockIdentifier }-${ address.id }` }
          block={ address }
          onAnswer={ onAddressAnswer }
        />
      </div>
    </div>
  );
};

QuestionSubstantialOwnerBlock.displayName = 'QuestionSubstantialOwnerBlock';

QuestionSubstantialOwnerBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionSubstantialOwnerBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionSubstantialOwnerBlock;
